// Tries — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/trees.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer (using ApproachVisualizer's trie-rendering support)
// wherever it clarifies the traversal.

const trieProblems = [
  {
    id: 'implement-trie-prefix-tree',
    title: 'Implement Trie (Prefix Tree)',
    difficulty: 'Medium',
    leetcode: { number: 208, url: 'https://leetcode.com/problems/implement-trie-prefix-tree/' },
    statement:
      'Implement a Trie with insert(word), search(word) — returns true only if the exact word was inserted — ' +
      'and startsWith(prefix) — returns true if any inserted word begins with that prefix.',
    example: {
      input: 'insert("apple"); search("apple"); search("app"); startsWith("app"); insert("app"); search("app")',
      output: 'true, false, true, true',
      note: '"apple" exists exactly, "app" doesn\'t (yet) — but it IS a valid prefix. After inserting "app" too, search("app") becomes true.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (A List of Inserted Words)',
        why:
          "Keep every inserted word in a plain list. insert() is just an append. search() checks the list for an " +
          "exact match. startsWith() checks whether ANY stored word begins with the given prefix. Correct, but " +
          "every search or prefix check has to scan through every word ever inserted.",
        steps: [
          'insert(word): append it to a list.',
          'search(word): scan the list for an exact match.',
          'startsWith(prefix): scan the list, checking each word with String.startsWith().',
          'Every operation after insert costs O(n · L) — n words, each up to L characters long.',
        ],
        code:
`class TrieBruteForce {
    private List<String> words = new ArrayList<>();

    public void insert(String word) {
        words.add(word);
    }

    public boolean search(String word) {
        return words.contains(word);
    }

    public boolean startsWith(String prefix) {
        for (String word : words) {
            if (word.startsWith(prefix)) return true;
        }
        return false;
    }
}`,
        complexity: { time: 'O(n · L) per search/startsWith call', space: 'O(n · L) — every word stored in full' },
        whenToUse: "Only useful to confirm the three operations' meanings out loud — the real trie structure below is what this problem is actually testing.",
      },
      {
        name: 'Approach 2 — Optimized: An Actual Trie',
        why:
          "Store characters as a branching tree instead of whole words in a flat list. Every operation becomes a " +
          "direct walk down existing nodes, one character at a time — completely independent of how many other " +
          "words happen to be stored.",
        steps: [
          'insert(word): walk from the root, creating a new child node for each character not already present, then mark the final node isEnd = true.',
          'search(word): walk the same way — if the walk ever fails (a needed child doesn\'t exist), return false. If it succeeds, return whether the final node is isEnd.',
          'startsWith(prefix): the same walk, but return true as soon as it succeeds — isEnd doesn\'t matter here.',
          'Every operation costs only O(word or prefix length) — the number of other words stored never factors in.',
        ],
        visual: {
          trie: { label: 'trie', value: {
            children: { a: { children: { p: { children: { p: { children: {
              l: { children: { e: { children: {}, isEnd: true } }, isEnd: false },
            }, isEnd: true } }, isEnd: false } }, isEnd: false } }, isEnd: false,
          } },
          varNames: [],
          steps: [
            { highlights: { trie: ['a', 'ap', 'app', 'appl', 'apple'] }, note: 'insert("apple"): walk a-p-p-l-e, creating a new node for each character. Mark the final node ("apple") isEnd = true.' },
            { highlights: { trie: ['app'] }, note: 'search("app"): the walk a-p-p succeeds (those nodes exist), but this node\'s isEnd is still false — "app" alone was never inserted. Returns false.' },
            { highlights: { trie: ['app'] }, note: 'startsWith("app"): same walk, but isEnd doesn\'t matter here — the walk itself succeeded. Returns true.' },
            { highlights: { trie: ['app'] }, note: 'insert("app"): walk a-p-p — all three nodes already exist, reused as-is. Just flip this node\'s isEnd to true. search("app") now returns true.' },
          ],
        },
        code:
`class Trie {
    private TrieNode root = new TrieNode();

    public void insert(String word) {
        TrieNode curr = root;
        for (char c : word.toCharArray()) {
            curr = curr.children.computeIfAbsent(c, k -> new TrieNode());
        }
        curr.isEnd = true;
    }

    public boolean search(String word) {
        TrieNode node = walk(word);
        return node != null && node.isEnd;
    }

    public boolean startsWith(String prefix) {
        return walk(prefix) != null;
    }

    private TrieNode walk(String s) {
        TrieNode curr = root;
        for (char c : s.toCharArray()) {
            curr = curr.children.get(c);
            if (curr == null) return null;
        }
        return curr;
    }

    static class TrieNode {
        Map<Character, TrieNode> children = new HashMap<>();
        boolean isEnd = false;
    }
}`,
        complexity: { time: 'O(L) per operation — L = word or prefix length', space: 'O(total characters across all inserted words)' },
        whenToUse: "This is the entire point of the pattern — the default, expected answer for this exact problem, and the direct foundation every other Tries problem builds on.",
      },
    ],
  },
  {
    id: 'design-add-and-search-words-data-structure',
    title: 'Design Add and Search Words Data Structure',
    difficulty: 'Medium',
    leetcode: { number: 211, url: 'https://leetcode.com/problems/design-add-and-search-words-data-structure/' },
    statement:
      'Design a data structure that supports addWord(word) and search(word), where search may contain \'.\' as ' +
      'a wildcard matching any single character.',
    example: {
      input: 'addWord("bad"); addWord("dad"); addWord("mad"); search("pad"); search("bad"); search(".ad"); search("b..")',
      output: 'false, true, true, true',
      note: '"pad" was never added. "bad" matches exactly. ".ad" matches any first letter followed by "ad" — matches "bad"/"dad"/"mad". "b.." matches "bad".',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check the Pattern Against Every Stored Word)',
        why:
          "Store every added word in a list. For search, compare the pattern against EVERY stored word " +
          "individually — a '.' in the pattern matches anything at that position, so this is a direct " +
          "character-by-character comparison per stored word.",
        steps: [
          'addWord(word): append it to a list.',
          'search(pattern): for each stored word of the same length, compare character by character.',
          'A \'.\' in the pattern always matches; any other character must match exactly.',
          'The moment one stored word matches the whole pattern, return true. Every word gets compared — O(n · L).',
        ],
        code:
`class WordDictionaryBruteForce {
    private List<String> words = new ArrayList<>();

    public void addWord(String word) {
        words.add(word);
    }

    public boolean search(String pattern) {
        for (String word : words) {
            if (word.length() != pattern.length()) continue;
            if (matches(word, pattern)) return true;
        }
        return false;
    }

    private boolean matches(String word, String pattern) {
        for (int i = 0; i < word.length(); i++) {
            if (pattern.charAt(i) != '.' && pattern.charAt(i) != word.charAt(i)) return false;
        }
        return true;
    }
}`,
        complexity: { time: 'O(n · L) per search', space: 'O(n · L)' },
        whenToUse: "Only useful to confirm what the wildcard means — every stored word gets fully compared even when most share nothing with the pattern.",
      },
      {
        name: 'Approach 2 — Optimized: Trie With Wildcard DFS',
        why:
          "Store every word in a trie, same as before. For a search with no wildcards, it's a direct walk. The " +
          "moment a '.' is hit, branch into every child at that level instead of just one — turning the search " +
          "into a small DFS exactly at the point wildcards demand it, while every non-wildcard character still " +
          "gets the fast direct-walk treatment.",
        steps: [
          'addWord(word): insert it into a trie, exactly like the standard Trie\'s insert().',
          'search(pattern): recursively walk the trie alongside the pattern\'s characters.',
          'For a literal character, follow that one specific child (or fail if it doesn\'t exist).',
          'For \'.\', try every child at that level — if ANY of them lead to a full match, the search succeeds.',
        ],
        visual: {
          trie: { label: 'trie (bad, dad, mad)', value: {
            children: {
              b: { children: { a: { children: { d: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false },
              d: { children: { a: { children: { d: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false },
              m: { children: { a: { children: { d: { children: {}, isEnd: true } }, isEnd: false } }, isEnd: false },
            }, isEnd: false,
          } },
          varNames: [],
          steps: [
            { highlights: { trie: [] }, note: 'search(".ad"): position 0 is \'.\' — try every child of the root: b, d, m.' },
            { highlights: { trie: ['b'] }, note: 'Trying branch \'b\' first: position 1 needs \'a\' — b has a child \'a\'. Continue.' },
            { highlights: { trie: ['ba', 'bad'] }, note: 'Position 2 needs \'d\' — \'a\' has a child \'d\', and that node IS isEnd. Match found via "bad" — search returns true immediately (branches \'d\' and \'m\' are never even tried).' },
          ],
        },
        code:
`class WordDictionary {
    private TrieNode root = new TrieNode();

    public void addWord(String word) {
        TrieNode curr = root;
        for (char c : word.toCharArray()) {
            curr = curr.children.computeIfAbsent(c, k -> new TrieNode());
        }
        curr.isEnd = true;
    }

    public boolean search(String pattern) {
        return dfs(root, pattern, 0);
    }

    private boolean dfs(TrieNode node, String pattern, int i) {
        if (i == pattern.length()) return node.isEnd;
        char c = pattern.charAt(i);

        if (c == '.') {
            for (TrieNode child : node.children.values()) {
                if (dfs(child, pattern, i + 1)) return true;
            }
            return false;
        }
        TrieNode child = node.children.get(c);
        return child != null && dfs(child, pattern, i + 1);
    }

    static class TrieNode {
        Map<Character, TrieNode> children = new HashMap<>();
        boolean isEnd = false;
    }
}`,
        complexity: { time: 'O(26^k · L) worst case, k = number of wildcards — but O(L) when there are none', space: 'O(total characters across all added words)' },
        interviewNote: "Worth naming the worst case honestly: a pattern of all dots (\"......\") degenerates toward checking every word, same as the brute force — the trie's win comes specifically from patterns with FEW wildcards relative to fixed characters.",
        whenToUse: "This is the standard answer — reach for a trie with branching DFS any time a search needs to support wildcards over a large, reusable dictionary of words.",
      },
    ],
  },
  {
    id: 'word-search-ii',
    title: 'Word Search II',
    difficulty: 'Hard',
    leetcode: { number: 212, url: 'https://leetcode.com/problems/word-search-ii/' },
    statement:
      'Given an m x n board of characters and a list of words, return all words from the list that can be ' +
      'formed by a path of sequentially adjacent cells (up/down/left/right), where the same cell can\'t be ' +
      'reused within one word.',
    example: {
      input: 'board = [["o","a","a","n"],["e","t","a","e"],["i","h","k","r"],["i","f","l","v"]], words = ["oath", "pea", "eat", "rain"]',
      output: '["eat", "oath"]',
      note: '"oath" and "eat" both trace valid adjacent paths on the board; "pea" and "rain" don\'t.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Backtrack the Board Separately for Every Word)',
        why:
          "For each word independently, run a full backtracking DFS over the board looking for that exact word " +
          "— the same technique as the classic single-word Word Search problem. Correct, but words that share a " +
          "prefix (like \"oat\" and \"oath\") re-walk the exact same starting cells from scratch, once per word.",
        steps: [
          'For each word in the list, try starting the search from every cell on the board.',
          'From a starting cell, backtrack to neighboring cells looking for the word\'s next character.',
          'Mark cells as visited during the current path, and unmark them when backtracking.',
          'If a full match is found for a word, record it — but every word repeats this entire board search independently.',
        ],
        code:
`public List<String> findWordsBruteForce(char[][] board, String[] words) {
    List<String> found = new ArrayList<>();
    for (String word : words) {
        if (existsOnBoard(board, word)) found.add(word);
    }
    return found;
}

private boolean existsOnBoard(char[][] board, String word) {
    for (int r = 0; r < board.length; r++) {
        for (int c = 0; c < board[0].length; c++) {
            if (backtrack(board, word, 0, r, c)) return true;
        }
    }
    return false;
}

private boolean backtrack(char[][] board, String word, int i, int r, int c) {
    if (i == word.length()) return true;
    if (r < 0 || c < 0 || r >= board.length || c >= board[0].length || board[r][c] != word.charAt(i)) return false;

    char temp = board[r][c];
    board[r][c] = '#';   // mark visited
    boolean found = backtrack(board, word, i + 1, r + 1, c) || backtrack(board, word, i + 1, r - 1, c)
                 || backtrack(board, word, i + 1, r, c + 1) || backtrack(board, word, i + 1, r, c - 1);
    board[r][c] = temp;   // unmark on backtrack
    return found;
}`,
        complexity: { time: 'O(words · rows · cols · 4^L)', space: 'O(L) — recursion depth per search' },
        whenToUse: "Fine for a small word list — but every word restarts the board search from scratch, even when several words share long common prefixes and could reuse the same partial board walk.",
      },
      {
        name: 'Approach 2 — Optimized: One Combined Backtrack, Guided by a Trie',
        why:
          "Build a single trie from ALL the words first. Then backtrack over the board ONCE, walking the trie " +
          "alongside the board path instead of comparing against one word at a time. Words sharing a prefix — " +
          "\"oat\" and \"oath\" — share the exact same board-walking steps for as long as their trie paths agree, " +
          "and a board path that stops matching ANY word's prefix is pruned immediately rather than being " +
          "retried separately for every word.",
        steps: [
          'Insert every word into a trie once, up front.',
          'Backtrack over the board starting from every cell, but track a matching TrieNode alongside the current board position instead of a target word index.',
          'At each cell, only continue if the trie has a child for that cell\'s letter — otherwise, prune this path immediately (no word can possibly continue this way).',
          'Whenever the current trie node is isEnd, a word was found — record it, then keep exploring further (a found word\'s node might still have children continuing to a longer word).',
        ],
        visual: {
          arrays: [{ label: 'board (single row, for a compact example)', values: ['o', 'a', 't', 'h'] }],
          trie: { label: 'trie of {oat, oath}', value: {
            children: { o: { children: { a: { children: { t: { children: {
              h: { children: {}, isEnd: true },
            }, isEnd: true } }, isEnd: false } }, isEnd: false } }, isEnd: false,
          } },
          varNames: [],
          steps: [
            { highlights: { board: [0], trie: ['o'] }, note: 'Board cell 0 = \'o\'. Trie root has a child \'o\' — continue. Board and trie move together.' },
            { highlights: { board: [0, 1], trie: ['o', 'oa'] }, note: 'Board cell 1 = \'a\'. Trie node \'o\' has a child \'a\' — continue.' },
            { highlights: { board: [0, 1, 2], trie: ['oat'] }, note: 'Board cell 2 = \'t\'. Trie node \'oa\' has a child \'t\', and THIS node is isEnd — "oat" found! Keep going anyway; this node still has a child.' },
            { highlights: { board: [0, 1, 2, 3], trie: ['oath'] }, note: 'Board cell 3 = \'h\'. Trie node \'oat\' has a child \'h\', also isEnd — "oath" found too, in the SAME board walk that already found "oat".' },
          ],
        },
        code:
`public List<String> findWords(char[][] board, String[] words) {
    TrieNode root = new TrieNode();
    for (String word : words) insert(root, word);

    List<String> found = new ArrayList<>();
    for (int r = 0; r < board.length; r++) {
        for (int c = 0; c < board[0].length; c++) {
            backtrack(board, r, c, root, found);
        }
    }
    return found;
}

private void backtrack(char[][] board, int r, int c, TrieNode node, List<String> found) {
    if (r < 0 || c < 0 || r >= board.length || c >= board[0].length || board[r][c] == '#') return;

    char ch = board[r][c];
    TrieNode next = node.children.get(ch);
    if (next == null) return;   // prune — no word continues this way

    if (next.word != null) {   // this node stores the full word once it's a complete match
        found.add(next.word);
        next.word = null;       // avoid duplicate results
    }

    board[r][c] = '#';
    backtrack(board, r + 1, c, next, found);
    backtrack(board, r - 1, c, next, found);
    backtrack(board, r, c + 1, next, found);
    backtrack(board, r, c - 1, next, found);
    board[r][c] = ch;
}`,
        complexity: { time: 'O(rows · cols · 4^L) — shared across all words via the trie, with early pruning', space: 'O(total characters across all words) — the trie' },
        interviewNote: "Storing the full matched word directly on its trie node (next.word = word, instead of just a boolean isEnd) is a small but handy trick — it means found words can be reported directly without separately reconstructing the string from the board path.",
        whenToUse: "This is the flagship problem combining Tries with backtracking (covered in full in its own pattern group later) — whenever many words need matching against the same board or grid, building one shared trie beats searching for each word independently.",
      },
    ],
  },
];

export default trieProblems;
